import React, { useState, useEffect } from 'react';
import { X, Building2, AlertCircle, Clock, Timer, ShieldCheck } from 'lucide-react';
import { condoApi } from '../../../services/api';
import { CustomerSettings } from '../../../types/customer/base';
import toast from 'react-hot-toast';

interface CustomerSettingsModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
}

export const CustomerSettingsModal: React.FC<CustomerSettingsModalProps> = ({
  open,
  onClose,
  customerId,
  customerName,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<CustomerSettings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load customer settings when modal opens
  useEffect(() => {
    if (open && customerId) {
      loadSettings();
    }
  }, [open, customerId]);

  const loadSettings = async () => {
    setLoading(true);
    setErrors({});
    try {
      const response = await condoApi.getCustomerSettings(customerId);
      const s = response.data;
      if (s?.visitor_settings) {
        s.visitor_settings.face_removal_delay_enabled ??= false;
        s.visitor_settings.face_removal_delay_minutes ??= 5;
      }
      setSettings(s);
    } catch (error: any) {
      console.error('Failed to load customer settings:', error);
      toast.error('Erro ao carregar configurações do cliente');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const formatCNPJ = (value: string): string => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');

    // Apply CNPJ mask: XX.XXX.XXX/XXXX-XX
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!settings) return false;

    // Validate basic info
    if (!settings.basic_info.name || settings.basic_info.name.trim() === '') {
      newErrors.name = 'Nome é obrigatório';
    }
    if (!settings.basic_info.address || settings.basic_info.address.trim() === '') {
      newErrors.address = 'Endereço é obrigatório';
    }
    if (!settings.basic_info.city || settings.basic_info.city.trim() === '') {
      newErrors.city = 'Cidade é obrigatória';
    }
    if (!settings.basic_info.state || settings.basic_info.state.trim() === '') {
      newErrors.state = 'Estado é obrigatório';
    }

    // Validate CNPJ if provided
    if (settings.basic_info.postal_code) {
      const digits = settings.basic_info.postal_code.replace(/\D/g, '');
      if (digits.length !== 14) {
        newErrors.postal_code = 'CNPJ deve ter 14 dígitos';
      }
    }

    // Validate visitor settings
    if (settings.visitor_settings.visitor_entry_window_minutes < 1 ||
        settings.visitor_settings.visitor_entry_window_minutes > 10080) {
      newErrors.entry_window = 'Janela de entrada deve estar entre 1 e 10080 minutos (1 minuto a 7 dias)';
    }
    if (settings.visitor_settings.visitor_exit_window_hours < 1 ||
        settings.visitor_settings.visitor_exit_window_hours > 720) {
      newErrors.exit_window = 'Janela de saída deve estar entre 1 e 720 horas (1 hora a 30 dias)';
    }
    if (settings.visitor_settings.face_removal_delay_enabled &&
        (settings.visitor_settings.face_removal_delay_minutes < 1 ||
         settings.visitor_settings.face_removal_delay_minutes > 60)) {
      newErrors.face_removal_delay = 'Entre 1 e 60 minutos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!settings || !validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await condoApi.updateCustomerSettings(customerId, {
        basic_info: {
          name: settings.basic_info.name,
          postal_code: settings.basic_info.postal_code?.replace(/\D/g, ''), // Remove formatting
          address: settings.basic_info.address,
          city: settings.basic_info.city,
          state: settings.basic_info.state,
          country: settings.basic_info.country || 'Brasil',
          phone: settings.basic_info.phone,
          email: settings.basic_info.email,
          description: settings.basic_info.description,
          internal_code: settings.basic_info.internal_code || undefined,
          portaria_type: settings.basic_info.portaria_type || 'none',
          phase: settings.basic_info.phase || 'disabled',
        },
        visitor_settings: {
          visitor_entry_window_minutes: settings.visitor_settings.visitor_entry_window_minutes,
          visitor_exit_window_hours: settings.visitor_settings.visitor_exit_window_hours,
          face_removal_delay_enabled: settings.visitor_settings.face_removal_delay_enabled,
          face_removal_delay_minutes: settings.visitor_settings.face_removal_delay_minutes,
        },
      });

      toast.success('Configurações atualizadas com sucesso!');
      onClose();
    } catch (error: any) {
      console.error('Failed to save customer settings:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao salvar configurações';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setErrors({});
    onClose();
  };

  const convertMinutesToReadable = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hora${hours !== 1 ? 's' : ''}`;
    return `${hours}h ${remainingMinutes}min`;
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleCancel} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-gray-950 border border-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-200">Configurações do Cliente</h2>
                <p className="text-sm text-gray-500 mt-0.5">{customerName}</p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              title="Fechar"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : settings ? (
              <div className="space-y-8 max-w-2xl">
                {/* Basic Information Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">Informações Básicas</h3>

                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nome <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={settings.basic_info.name}
                        onChange={(e) => setSettings({
                          ...settings,
                          basic_info: { ...settings.basic_info, name: e.target.value }
                        })}
                        className={`w-full px-4 py-2 bg-gray-900 border ${
                          errors.name ? 'border-red-500' : 'border-gray-700'
                        } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Nome do cliente"
                      />
                      {errors.name && (
                        <p className="text-xs text-red-400 mt-1">{errors.name}</p>
                      )}
                    </div>

                    {/* Internal Code */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Código Interno
                        <span className="ml-2 text-xs text-gray-500 font-normal">opcional — exibido em destaque no seletor</span>
                      </label>
                      <input
                        type="text"
                        value={settings.basic_info.internal_code || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          basic_info: { ...settings.basic_info, internal_code: e.target.value }
                        })}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: 001, ABC, VIA-01"
                      />
                    </div>

                    {/* Portaria Type + Phase */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Tipo de Portaria
                        </label>
                        <select
                          value={settings.basic_info.portaria_type || 'none'}
                          onChange={(e) => setSettings({ ...settings, basic_info: { ...settings.basic_info, portaria_type: e.target.value as any } })}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="none">Nenhuma</option>
                          <option value="presencial">Presencial</option>
                          <option value="autonoma">Autônoma</option>
                          <option value="virtual">Virtual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Fase
                        </label>
                        <select
                          value={settings.basic_info.phase || 'disabled'}
                          onChange={(e) => setSettings({ ...settings, basic_info: { ...settings.basic_info, phase: e.target.value as any } })}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="disabled">Desativado</option>
                          <option value="implementation">Implementação</option>
                          <option value="production">Produção</option>
                        </select>
                      </div>
                    </div>

                    {/* CNPJ */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        CNPJ
                      </label>
                      <input
                        type="text"
                        value={settings.basic_info.postal_code ? formatCNPJ(settings.basic_info.postal_code) : ''}
                        onChange={(e) => {
                          const formatted = formatCNPJ(e.target.value);
                          setSettings({
                            ...settings,
                            basic_info: { ...settings.basic_info, postal_code: formatted }
                          });
                        }}
                        className={`w-full px-4 py-2 bg-gray-900 border ${
                          errors.postal_code ? 'border-red-500' : 'border-gray-700'
                        } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                      />
                      {errors.postal_code && (
                        <p className="text-xs text-red-400 mt-1">{errors.postal_code}</p>
                      )}
                    </div>

                    {/* Phone and Email */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Telefone
                        </label>
                        <input
                          type="text"
                          value={settings.basic_info.phone || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            basic_info: { ...settings.basic_info, phone: e.target.value }
                          })}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="(11) 98765-4321"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={settings.basic_info.email || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            basic_info: { ...settings.basic_info, email: e.target.value }
                          })}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="contato@example.com"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Endereço <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={settings.basic_info.address}
                        onChange={(e) => setSettings({
                          ...settings,
                          basic_info: { ...settings.basic_info, address: e.target.value }
                        })}
                        className={`w-full px-4 py-2 bg-gray-900 border ${
                          errors.address ? 'border-red-500' : 'border-gray-700'
                        } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Rua, número, complemento"
                      />
                      {errors.address && (
                        <p className="text-xs text-red-400 mt-1">{errors.address}</p>
                      )}
                    </div>

                    {/* City and State */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Cidade <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={settings.basic_info.city}
                          onChange={(e) => setSettings({
                            ...settings,
                            basic_info: { ...settings.basic_info, city: e.target.value }
                          })}
                          className={`w-full px-4 py-2 bg-gray-900 border ${
                            errors.city ? 'border-red-500' : 'border-gray-700'
                          } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="São Paulo"
                        />
                        {errors.city && (
                          <p className="text-xs text-red-400 mt-1">{errors.city}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Estado <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={settings.basic_info.state}
                          onChange={(e) => setSettings({
                            ...settings,
                            basic_info: { ...settings.basic_info, state: e.target.value }
                          })}
                          className={`w-full px-4 py-2 bg-gray-900 border ${
                            errors.state ? 'border-red-500' : 'border-gray-700'
                          } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="SP"
                          maxLength={2}
                        />
                        {errors.state && (
                          <p className="text-xs text-red-400 mt-1">{errors.state}</p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Descrição
                      </label>
                      <textarea
                        value={settings.basic_info.description || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          basic_info: { ...settings.basic_info, description: e.target.value }
                        })}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="Informações adicionais sobre o cliente"
                      />
                    </div>
                  </div>
                </div>

                {/* Visitor Settings Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-4">Configurações de Visitantes</h3>

                  {/* Info Banner */}
                  <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300">
                      Estas configurações afetam todos os visitantes autorizados para este cliente. As janelas de tempo determinam quando um visitante pode entrar pela primeira vez e quanto tempo pode permanecer no local.
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Entry Window */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <div className="flex items-center gap-2">
                          <Timer className="h-4 w-4 text-gray-400" />
                          Janela de Entrada (minutos)
                        </div>
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={10080}
                        value={settings.visitor_settings.visitor_entry_window_minutes}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setSettings({
                            ...settings,
                            visitor_settings: {
                              ...settings.visitor_settings,
                              visitor_entry_window_minutes: value
                            }
                          });
                        }}
                        className={`w-full px-4 py-2 bg-gray-900 border ${
                          errors.entry_window ? 'border-red-500' : 'border-gray-700'
                        } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Tempo após autorização que o visitante pode entrar pela primeira vez
                      </p>
                      <p className="text-xs text-blue-400 mt-1">
                        {convertMinutesToReadable(settings.visitor_settings.visitor_entry_window_minutes)}
                      </p>
                      {errors.entry_window && (
                        <p className="text-xs text-red-400 mt-1">{errors.entry_window}</p>
                      )}
                    </div>

                    {/* Exit Window */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          Janela de Saída (horas)
                        </div>
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={720}
                        value={settings.visitor_settings.visitor_exit_window_hours}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setSettings({
                            ...settings,
                            visitor_settings: {
                              ...settings.visitor_settings,
                              visitor_exit_window_hours: value
                            }
                          });
                        }}
                        className={`w-full px-4 py-2 bg-gray-900 border ${
                          errors.exit_window ? 'border-red-500' : 'border-gray-700'
                        } rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Tempo total que o visitante pode permanecer e sair (a partir da autorização)
                      </p>
                      {errors.exit_window && (
                        <p className="text-xs text-red-400 mt-1">{errors.exit_window}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-800 flex-shrink-0 bg-gray-950">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
